import React, { useMemo } from 'react';
import ReactFlow, { 
  Node, 
  Edge, 
  Background, 
  Controls, 
  MiniMap
} from 'reactflow';
import 'reactflow/dist/style.css';
import { 
  TaskDialogueState, 
  TASK_STATE_TRANSITIONS,
  TASK_STATE_METADATA 
} from '../../DialogueManagementTaskClassification/TaskClassificationDialogueBlueprint';

interface TaskGraphProps {
  currentState?: TaskDialogueState;
}

const TaskGraph: React.FC<TaskGraphProps> = ({ currentState }) => {
  const { nodes, edges } = useMemo(() => {
    const nodeMap = new Map<string, Node>();
    const primaryEdges: Edge[] = [];
    const escapeEdges: Edge[] = [];
    
    // Define the main task flow path (left to right)
    const mainPath = [
      'processing_options_selection',
      'variant_selection',
      'amount_clarification',
      'task_completed'
    ];
    
    // Define escape/side states (positioned above/below main path)
    const escapeStates = [
      'no_tasks_found',
      'abort_confirmation',
      'task_aborted'
    ];
    
    // Define common escape actions that appear in multiple states
    const commonEscapeActions = ['ABORT', 'SOMETHING_ELSE'];
    
    // Position main path nodes vertically (top -> down)
    const baseX = 300;
    const spacingY = 140;
    mainPath.forEach((state, index) => {
      const metadata = TASK_STATE_METADATA[state as TaskDialogueState];
      const isCurrent = state === currentState;
      
      nodeMap.set(state, {
        id: state,
        type: 'default',
        position: { 
          x: baseX, 
          y: index * spacingY + 40 
        },
        data: { 
          label: state.replace(/_/g, ' '),
          metadata: metadata?.entryEffectKey || '',
          isTerminal: metadata?.isTerminal || false,
          hasEscapeActions: Object.keys(TASK_STATE_TRANSITIONS[state as TaskDialogueState] || {})
            .some(action => commonEscapeActions.includes(action))
        },
        style: {
          background: isCurrent ? '#e8f5e8' : metadata?.isTerminal ? '#f3e5f5' : '#fff',
          border: isCurrent ? '2px solid #4caf50' : metadata?.isTerminal ? '2px solid #9c27b0' : '1px solid #ccc',
          borderRadius: '8px',
          padding: '10px',
          fontSize: '12px',
          fontWeight: isCurrent ? 'bold' : 'normal',
          minWidth: '120px'
        }
      });
    });
    
    // Position escape states to the left/right of the main path
    escapeStates.forEach((state, index) => {
      const metadata = TASK_STATE_METADATA[state as TaskDialogueState];
      const isCurrent = state === currentState;
      
      // Alternate between left and right; stagger down alongside the main path
      const yOffset = Math.floor(index / 2) * spacingY + 40;
      const xOffset = (index % 2 === 0) ? baseX - 220 : baseX + 300;
      
      nodeMap.set(state, {
        id: state,
        type: 'default',
        position: { 
          x: xOffset, 
          y: yOffset 
        },
        data: { 
          label: state.replace(/_/g, ' '),
          metadata: metadata?.entryEffectKey || '',
          isTerminal: metadata?.isTerminal || false,
          hasEscapeActions: Object.keys(TASK_STATE_TRANSITIONS[state as TaskDialogueState] || {})
            .some(action => commonEscapeActions.includes(action))
        },
        style: {
          background: isCurrent ? '#e8f5e8' : metadata?.isTerminal ? '#f3e5f5' : '#fff',
          border: isCurrent ? '2px solid #4caf50' : metadata?.isTerminal ? '2px solid #9c27b0' : '1px solid #ccc',
          borderRadius: '8px',
          padding: '10px',
          fontSize: '12px',
          fontWeight: isCurrent ? 'bold' : 'normal',
          minWidth: '120px'
        }
      });
    });
    
    // Helper to detect escape triggers robustly
    const isEscapeTrigger = (trigger: string) => {
      const normalized = trigger.toUpperCase();
      return (
        normalized.includes('ABORT') ||
        normalized.includes('SOMETHING_ELSE')
      );
    };

    // Create primary flow edges (non-escape actions)
    Object.entries(TASK_STATE_TRANSITIONS).forEach(([fromState, transitions]) => {
      Object.entries(transitions).forEach(([trigger, toState]) => {
        if (toState && !isEscapeTrigger(trigger)) {
          primaryEdges.push({
            id: `${fromState}-${trigger}-${toState}`,
            source: fromState,
            target: toState,
            label: trigger.replace(/_/g, ' ').toLowerCase(),
            style: { 
              stroke: '#4caf50', 
              strokeWidth: 2,
              fontSize: '10px' 
            },
            labelStyle: { 
              fontSize: '10px', 
              fill: '#4caf50',
              fontWeight: 'bold'
            }
          });
        }
      });
    });
    
    // Create escape action edges (dashed, lighter color) - but group common ones
    const escapeActionGroups = new Map<string, string[]>();
    
    Object.entries(TASK_STATE_TRANSITIONS).forEach(([fromState, transitions]) => {
      Object.entries(transitions).forEach(([trigger, toState]) => {
        if (toState && isEscapeTrigger(trigger)) {
          const key = `${fromState}-${toState}`;
          if (!escapeActionGroups.has(key)) {
            escapeActionGroups.set(key, []);
          }
          escapeActionGroups.get(key)!.push(trigger);
        }
      });
    });
    
    // Create grouped escape edges (no labels to avoid clutter)
    escapeActionGroups.forEach((triggers, key) => {
      const [fromState, toState] = key.split('-');
      
      escapeEdges.push({
        id: `escape-${key}`,
        source: fromState,
        target: toState,
        style: { 
          stroke: '#999', 
          strokeWidth: 1,
          strokeDasharray: '8,4',
          fontSize: '10px' 
        },
        labelStyle: { }
      });
    });
    
    return {
      nodes: Array.from(nodeMap.values()),
      // Make sure escape edges render first (under) by ordering before primary edges
      edges: [...escapeEdges, ...primaryEdges]
    };
  }, [currentState]);
  
  return (
    <div style={{ width: '100%', height: '600px', border: '1px solid #ddd' }}>
      <div style={{ padding: '8px', fontSize: '12px', background: '#f5f5f5', borderBottom: '1px solid #ddd' }}>
        <strong>Task Classification Flow:</strong>
        <span style={{ color: '#4caf50', marginLeft: '8px' }}>━━━</span> Primary flow
        <span style={{ color: '#999', marginLeft: '16px' }}>┅┅┅</span> Escape actions (grouped)
        <span style={{ color: '#9c27b0', marginLeft: '16px' }}>■</span> Terminal state
        {currentState && (
          <span style={{ color: '#2e7d32', marginLeft: '16px', fontWeight: 'bold' }}>
            🎯 Current: {currentState.replace(/_/g, ' ')}
          </span>
        )}
      </div>
              <ReactFlow
                nodes={nodes}
                edges={edges}
                fitView
                nodesDraggable={true}
                nodesConnectable={false}
                elementsSelectable={true}
                attributionPosition="bottom-left"
              >
        <Background />
        <Controls />
        <MiniMap />
      </ReactFlow>
    </div>
  );
};

export default TaskGraph;
